import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { IBuyerUser, PROFILE_STAGE, BUYER_USER_ROLE } from 'domain/entities/BuyerUser/BuyerUser';
import { IMatch, MATCH_STATUSES } from 'domain/entities/Match/Match';
import { BuyerMatchQuery } from 'interfaceAdapter/queries/BuyerMatch';
import { useLoading } from 'application/contexts/useLoading';
import { diContainerContext } from 'application/contexts/useDiContainer';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

type BuyerUserContext = {
    /** ログイン中のユーザー */
    user: IBuyerUser | null;
    setUser: (user: IBuyerUser) => void;
    resetUser: () => void;

    /** ログイン後に遷移するURL */
    urlAfterLogin: string;
    /** ログイン後URLを設定 */
    setUrlAfterLogin: (url: string) => void;

    waitingFirstContactCount: number;
    needFirstContactMeetRoomIds: string[];
    existsUnreadMsg: boolean;
    isMeetingDateNotSet: boolean; // 面談日未設定のマッチングがあるか
    offerList: IMatch[]; // マッチング前のオファー
    matchList: IMatch[];
    meetingDateSetMatchList: IMatch[]; // 面談日が設定されているマッチング
    notCompletedProfile: boolean; // プロフィールが未完成
};

const defaultContext: BuyerUserContext = {
    user: null,
    setUser: () => {},
    resetUser: () => {},
    urlAfterLogin: '/buyer/dashboard/',
    setUrlAfterLogin: () => {},
    waitingFirstContactCount: 0,
    needFirstContactMeetRoomIds: [],
    existsUnreadMsg: false,
    isMeetingDateNotSet: false,
    offerList: [],
    matchList: [],
    meetingDateSetMatchList: [],
    notCompletedProfile: false,
};

export const buyerUserContext = createContext<BuyerUserContext>(defaultContext);
export const useBuyerUser = (): BuyerUserContext => {
    const [user, setUser] = useState<IBuyerUser | null>(null);
    const [urlAfterLogin, setUrlAfterLogin] = useState('/buyer/dashboard/');
    const [matchList, setMatchList] = useState<IMatch[]>([]);
    const [meetingDateSetMatchList, setMeetingDateSetMatchList] = useState<IMatch[]>([]);
    const [offerList, setOfferList] = useState<IMatch[]>([]);
    const [existsUnreadMsg, setExistsUnreadMsg] = useState(false);
    const [isMeetingDateNotSet, setIsMeetingDateNotSet] = useState(false);
    const [notCompletedProfile, setNotCompletedProfile] = useState(false);
    const { setLoading } = useLoading();
    const today = dayjs();
    const diContainer = useContext(diContainerContext);
    const buyerMatchQuery = diContainer.resolve(BuyerMatchQuery);
    const navigate = useNavigate();
    const resetUser = useCallback(async () => {
        setUser(null);
    }, []);

    useEffect(() => {
        if (!user) {
            return;
        }

        if (user.role === BUYER_USER_ROLE.USER && user.account_profile_stage === PROFILE_STAGE.NEED_USER_PROFILE) {
            setNotCompletedProfile(true);
            navigate('/buyer/registration/user_profile/');
        }

        const fetchMatchList = async () => {
            if(!user.buyer) {
                return;
            }
            setLoading(true);
            try {
                const matchList = await buyerMatchQuery.fetchList();
                setMatchList(matchList.filter((match) => match.status === MATCH_STATUSES.MATCH_STATUS_CONNECTED));
                setOfferList(matchList.filter((match) => match.status === MATCH_STATUSES.MATCH_STATUS_WAITING_ACCEPT));
                setMeetingDateSetMatchList(
                    matchList.filter(
                        (match) =>
                            match.status === MATCH_STATUSES.MATCH_STATUS_CONNECTED &&
                            match.meeting_date &&
                            dayjs(match.meeting_date).diff(today, 'day') >= 0,
                    ),
                );
                // マッチングから1日以上経過しているが、面談日が未設定
                setIsMeetingDateNotSet(
                    matchList.some(
                        (match) =>
                            match.status === MATCH_STATUSES.MATCH_STATUS_CONNECTED &&
                            !match.meeting_date &&
                            dayjs(match.date_matched).diff(today, 'day') <= 1,
                    ),
                );
                // メッセージ未読
                setExistsUnreadMsg(matchList.some((match) => match.chat_room && match.chat_room.unread));
            } finally {
                setLoading(false);
            }
        };
        fetchMatchList();
    }, [user]);

    return {
        user,
        setUser,
        resetUser,

        urlAfterLogin,
        setUrlAfterLogin,

        waitingFirstContactCount: 0,
        needFirstContactMeetRoomIds: [],
        existsUnreadMsg,
        isMeetingDateNotSet,
        offerList,
        matchList,
        meetingDateSetMatchList,
        notCompletedProfile,
    };
};
